import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_CONTENT_LOCALE,
  type LocaleOption,
  type LessonMetadataTranslationPatch,
  type LessonMetadataTranslationProjection,
} from '@course-authoring/shared-types';
import { useEditorStore } from '../store/editorStore';
import { fetchWithAuthRetry, parseApiError, type ApiError } from './apiUtils';
import { contentLocaleKeys } from './contentLocaleApi';
import { lessonKeys, withEditedPublishState, type LessonEditorData } from './courseApi';
import { invalidateLessonLocaleCoverage } from './lessonLocaleCoverageQueries';
import { lessonListKeys } from './courseRecordApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export const lessonMetadataKeys = {
  all: ['lessonMetadata'] as const,
  details: () => [...lessonMetadataKeys.all, 'detail'] as const,
  detail: (lessonId: string, locale = DEFAULT_CONTENT_LOCALE, accountId: number | null = null) =>
    [...lessonMetadataKeys.details(), lessonId, locale, accountId ?? 'base'] as const,
};

function metadataUrl(lessonId: string, locale: string, accountId: number | null): string {
  const params = new URLSearchParams({ locale });
  if (accountId !== null) params.set('accountId', String(accountId));
  return `${API_BASE_URL}/lessons/${lessonId}/metadata?${params}`;
}

export async function fetchLessonMetadata(
  lessonId: string,
  locale = DEFAULT_CONTENT_LOCALE,
  accountId: number | null = null,
): Promise<LessonMetadataTranslationProjection> {
  const response = await fetchWithAuthRetry(metadataUrl(lessonId, locale, accountId));
  if (!response.ok) throw await parseApiError(response, 'Failed to fetch lesson metadata');
  return response.json();
}

export async function patchLessonMetadata(
  lessonId: string,
  patch: LessonMetadataTranslationPatch,
  locale = DEFAULT_CONTENT_LOCALE,
  accountId: number | null = null,
): Promise<LessonMetadataTranslationProjection> {
  const response = await fetchWithAuthRetry(metadataUrl(lessonId, locale, accountId), {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  if (!response.ok) throw await parseApiError(response, 'Failed to save lesson metadata');
  return response.json();
}

export function useLessonMetadataQuery(
  lessonId: string,
  options?: { enabled?: boolean; locale?: string; accountId?: number | null },
) {
  const locale = options?.locale ?? DEFAULT_CONTENT_LOCALE;
  const accountId = options?.accountId ?? null;
  return useQuery({
    queryKey: lessonMetadataKeys.detail(lessonId, locale, accountId),
    queryFn: () => fetchLessonMetadata(lessonId, locale, accountId),
    enabled: options?.enabled ?? Boolean(lessonId),
    staleTime: 0,
    retry: (failureCount, error: unknown) =>
      Boolean(error && typeof error === 'object' && 'retryable' in error && (error as ApiError).retryable)
      && failureCount < 3,
  });
}

export function useLessonMetadataMutation(
  lessonId: string,
  options?: {
    locale?: string;
    accountId?: number | null;
    onSuccess?: (data: LessonMetadataTranslationProjection) => void;
    onError?: (error: ApiError) => void;
  },
) {
  const queryClient = useQueryClient();
  const locale = options?.locale ?? DEFAULT_CONTENT_LOCALE;
  const accountId = options?.accountId ?? null;

  return useMutation({
    mutationFn: (patch: LessonMetadataTranslationPatch) =>
      patchLessonMetadata(lessonId, patch, locale, accountId),
    onSuccess: (data) => {
      queryClient.setQueryData(lessonMetadataKeys.detail(lessonId, locale, accountId), data);

      const localeOption = queryClient
        .getQueryData<LocaleOption[]>(contentLocaleKeys.all)
        ?.find((option) => option.locale === locale) ?? { locale, label: locale };
      const lessonDetailKey = lessonKeys.detail(lessonId, locale, accountId);
      queryClient.setQueryData<LessonEditorData>(
        lessonDetailKey,
        (previous) => previous
          ? {
              ...previous,
              lessonMeta: {
                ...previous.lessonMeta,
                title: data.title,
                description: data.description,
                availableLocales: mergeLocaleOption(previous.lessonMeta.availableLocales, localeOption),
                ...(locale === DEFAULT_CONTENT_LOCALE
                  ? {
                      estimatedTimeMinutes: data.estimatedTimeMinutes ?? undefined,
                      themeColor: data.themeColor ?? undefined,
                    }
                  : {}),
              },
              // NA-296: this save just happened, so the session is no longer
              // cancelable — force it now rather than wait for the
              // invalidation below to land.
              publishState: withEditedPublishState(previous),
            }
          : previous,
      );
      invalidateLessonLocaleCoverage(queryClient, lessonDetailKey);

      if (locale === DEFAULT_CONTENT_LOCALE) {
        // The list shows each lesson's default-locale title, so only a
        // default-locale save can leave its cached rows stale.
        void queryClient.invalidateQueries({ queryKey: lessonListKeys.all });

        const state = useEditorStore.getState();
        if (state.lessonMeta.id === lessonId) {
          state.updateLessonMeta({
            title: data.title,
            description: data.description,
            estimatedTimeMinutes: data.estimatedTimeMinutes ?? undefined,
            themeColor: data.themeColor ?? undefined,
          });
        }
      }

      if (accountId === null) {
        queryClient.removeQueries({
          queryKey: [...lessonMetadataKeys.details(), lessonId],
          type: 'inactive',
        });
      }
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      // Network failures reject with a bare TypeError, not an ApiError shape;
      // coerce so the consumer's error handling (logging, status) always runs.
      const apiError: ApiError = error && typeof error === 'object' && 'status' in error
        ? (error as ApiError)
        : {
            status: 0,
            code: 'NETWORK_ERROR',
            message: error instanceof Error ? error.message : 'Network error',
            retryable: true,
          };
      options?.onError?.(apiError);
    },
    retry: false,
  });
}

function mergeLocaleOption(
  options: LocaleOption[] | undefined,
  localeOption: LocaleOption,
): LocaleOption[] {
  const existing = options ?? [];
  return existing.some((option) => option.locale === localeOption.locale)
    ? existing
    : [...existing, localeOption];
}
